export type BuyerGuideSection = {
  title: string;
  copy: string;
  checks: string[];
};

export type BuyerGuideLink = {
  href: string;
  label: string;
  copy: string;
};

export type BuyerGuide = {
  number: string;
  slug: string;
  kicker: string;
  title: string;
  cardCopy: string;
  intro: string;
  metaTitle: string;
  metaDescription: string;
  sections: BuyerGuideSection[];
  nextSteps: BuyerGuideLink[];
  quoteTitle: string;
  quoteCopy: string;
};

export const buyerGuides: BuyerGuide[] = [
  {
    number: "01",
    slug: "choose-partition-materials",
    kicker: "Material selection guide",
    title: "How to choose commercial bathroom partition materials.",
    cardCopy: "Compare the five material directions around moisture, traffic, appearance, maintenance, and the actual project conditions.",
    intro: "A material decision starts with the room, not a universal winner. Compare the use of the restroom, moisture exposure, traffic, finish direction, maintenance expectations, mounting conditions, and approved project requirements before narrowing the partition system.",
    metaTitle: "Choose Bathroom Partition Materials | Partition Stalls",
    metaDescription: "Compare five commercial bathroom partition materials by moisture, traffic, design direction, maintenance, and project requirements.",
    sections: [
      {
        title: "Start with the room conditions.",
        copy: "A dry, climate-controlled office restroom and a high-use facility with frequent moisture or aggressive cleaning may call for different material conversations. Describe the actual environment before choosing a finish or price direction.",
        checks: [
          "Is the room dry, humid, or exposed to direct water?",
          "How much daily traffic and cleaning will the restroom receive?",
          "What wall, floor, ceiling, and mounting conditions are already known?",
        ],
      },
      {
        title: "Use the five material guides to narrow the direction.",
        copy: "Powder Coated Steel, Plastic Laminate, Solid Plastic (HDPE), Stainless Steel, and Phenolic each create a different maintenance, appearance, and project-planning conversation. The material pages explain the starting direction; the manufacturer system and approved project documents decide the final configuration.",
        checks: [
          "Powder Coated Steel is a familiar painted-metal direction for many commercial interiors.",
          "Plastic Laminate supports decorative colors, patterns, and wood-inspired looks in dry, climate-controlled interiors.",
          "Solid Plastic (HDPE), Stainless Steel, and Phenolic offer distinct directions for demanding use, moisture considerations, or an architectural finish expression.",
        ],
      },
      {
        title: "Confirm the system, not only the panel.",
        copy: "The same material can be offered in different panel thicknesses, finishes, hardware packages, mounting styles, privacy directions, and manufacturer series. A useful request makes those open decisions visible instead of assuming every material is one fixed product.",
        checks: [
          "Choose the closest material direction first.",
          "Review mounting, privacy, hardware, and accessory coordination next.",
          "Confirm the final manufacturer, series, finish, and project requirements before ordering.",
        ],
      },
    ],
    nextSteps: [
      { href: "/bathroom-partitions", label: "Compare all five materials", copy: "Open the material comparison and follow the direction that fits the room." },
      { href: "/resources/mounting-styles", label: "Review mounting styles", copy: "See how floor, headrail, and overhead support affect the system." },
      { href: "/resources/colors-finishes", label: "Coordinate colors and finishes", copy: "Use material first, then review the available finish direction." },
    ],
    quoteTitle: "Need help narrowing the material direction?",
    quoteCopy: "Send the project location, room conditions, photos or drawings, approximate quantity, and the finish direction you are considering. Partition Stalls will help organize the remaining questions before pricing.",
  },
  {
    number: "02",
    slug: "plan-partition-budget",
    kicker: "Budget planning guide",
    title: "How to plan a commercial bathroom partition budget.",
    cardCopy: "Use a transparent planning range and a small set of real project inputs before asking for final partition pricing.",
    intro: "An early budget works best when it is clear about what it includes and what still needs to be confirmed. Start with a material direction, approximate compartment count, and whether extra privacy or installation belongs in the early plan.",
    metaTitle: "Plan a Bathroom Partition Budget | Partition Stalls",
    metaDescription: "Plan a commercial bathroom partition budget by material, compartment count, and fixed privacy or installation additions.",
    sections: [
      {
        title: "Start with a planning range, not a final price.",
        copy: "The Partition Stalls calculator uses owner-reviewed material planning ranges and optional fixed additions to support an early comparison. It is not checkout, an order total, a price guarantee, or a substitute for a project-specific quote.",
        checks: [
          "Choose a material direction and approximate compartment count.",
          "Choose extra privacy or installation only when it belongs in the early plan.",
          "Use the result to establish a conversation range before final scope is known.",
        ],
      },
      {
        title: "Use the fixed additions only when selected.",
        copy: "Extra privacy adds $150 per selected stall to both ends of the planning range. Including installation adds $350 per selected stall to both ends. These are early planning additions, not a final installed price.",
        checks: [
          "Extra privacy is optional and adds $150 per selected stall.",
          "Installation is optional and adds $350 per selected stall.",
          "Accessibility is captured for quote review and does not change the public planning range.",
        ],
      },
      {
        title: "List the details that move a range toward a quote.",
        copy: "Dimensions, selected manufacturer or series, mounting, privacy, screens, accessories, freight, tax, installation scope, building access, and other project conditions are confirmed for the actual job. Sending plans or a clear sketch helps identify those factors sooner.",
        checks: [
          "Keep the planning result with the selected material, privacy, and installation directions.",
          "Add plans, measurements, photos, and known partition or accessory quantities.",
          "Ask for a project quote once the known scope is collected.",
        ],
      },
    ],
    nextSteps: [
      { href: "/partition-budget-calculator", label: "Open the budget calculator", copy: "Build an owner-reviewed planning range for the closest project direction." },
      { href: "/resources/how-to-measure", label: "Prepare measurements", copy: "Collect dimensions and conditions that make a pricing conversation more focused." },
      { href: "/resources/privacy-options", label: "Review privacy options", copy: "Add panel height and floor-clearance direction to the early scope." },
    ],
    quoteTitle: "Have an early budget direction ready?",
    quoteCopy: "Carry the calculator result into a quote request, or send the material, compartment count, privacy or installation direction, plans, and any known accessory scope. Final pricing is confirmed for the actual project.",
  },
  {
    number: "03",
    slug: "prepare-partition-quote",
    kicker: "Quote preparation guide",
    title: "What to send with a commercial bathroom partition quote.",
    cardCopy: "Send the few details that help identify the room, material direction, quantities, and unknowns without delaying an early conversation.",
    intro: "You do not need finished drawings to begin a quote request. Start with the information you have, then add plans, a hand sketch, measurements, photos, and open questions when they are available. Clear inputs reduce avoidable follow-up and make it easier to focus the next discussion.",
    metaTitle: "What to Send for a Partition Quote | Partition Stalls",
    metaDescription: "Prepare a commercial bathroom partition quote with location, materials, quantities, plans, drawings, photos, mounting, privacy, and accessory scope.",
    sections: [
      {
        title: "Begin with the five essential boxes.",
        copy: "The quote form starts with name, email, phone, project city, and project state. Those basics give Partition Stalls a way to understand where the project is and reply with the next useful question.",
        checks: [
          "Name and email for the project contact.",
          "Phone, project city, and project state.",
          "An early request is still useful when the full scope is not finalized.",
        ],
      },
      {
        title: "Upload plans or drawings when you have them.",
        copy: "The main quote form includes a visible upload area for plans or drawings. A clear floor plan, partition elevation, hand sketch, or marked-up photo can show relationships that a simple quantity cannot.",
        checks: [
          "Show room dimensions, fixtures, door swings, and wall returns when known.",
          "Include wide photos for the room and close photos for existing hardware or attachment details.",
          "Label any dimension or condition that still needs field verification.",
        ],
      },
      {
        title: "Add the details that shape the complete scope.",
        copy: "Use Additional options in the quote form for project type, material preference, quantity, project details, mounting direction, privacy goals, finish direction, accessories, building access, delivery, or schedule context. The goal is to identify what is known and what needs confirmation.",
        checks: [
          "Approximate stall, screen, and accessory quantities.",
          "Material, mounting, privacy, hardware, and finish direction if known.",
          "Project-specific delivery, access, installation, or timing questions.",
        ],
      },
    ],
    nextSteps: [
      { href: "/request-a-quote", label: "Start a quote request", copy: "Complete the essentials, upload plans or drawings, then add any additional project information." },
      { href: "/resources/how-to-measure", label: "Use the measuring guide", copy: "Prepare a room plan, fixture locations, surfaces, openings, and photos." },
      { href: "/commercial-restroom-accessories#catalog-results", label: "Build an accessory list", copy: "Search supplied Bobrick, ASI, and Bradley models, save the selections you need, then send the Quote List for a project quote." },
    ],
    quoteTitle: "Ready to share the project information you have?",
    quoteCopy: "Start the secure quote form with the five essential fields. Plans and drawings stay visible on the main form, while the additional options remain available if you want to add more detail.",
  },
  {
    number: "04",
    slug: "evaluate-partition-supplier",
    kicker: "Supplier evaluation guide",
    title: "How to evaluate a commercial bathroom partition supplier.",
    cardCopy: "Use the same project questions for every option so the material, mounting, accessories, pricing boundary, and next steps are clear.",
    intro: "A useful supplier conversation is specific to the project. Instead of comparing broad promises, compare how each option handles the actual room, the material and system being considered, the complete restroom scope, and the details that must be confirmed before an order is accepted.",
    metaTitle: "Evaluate a Bathroom Partition Supplier | Partition Stalls",
    metaDescription: "Evaluate a commercial bathroom partition supplier by project scope, material, mounting, accessories, quote boundaries, and confirmation steps.",
    sections: [
      {
        title: "Check whether the conversation starts with the real scope.",
        copy: "The right questions identify the project location, facility type, room conditions, compartment and screen quantities, existing conditions, material direction, and the drawings or photos available. A generic category name alone rarely resolves the system needed for the room.",
        checks: [
          "Can the supplier discuss the actual partition and accessory scope?",
          "Are mounting, privacy, walls, floors, ceilings, and access conditions considered?",
          "Are unknowns identified rather than silently assumed?",
        ],
      },
      {
        title: "Compare the complete system.",
        copy: "A partition package is more than a panel. Compare material, manufacturer or series when known, finish, panel and door details, hardware, mounting, screens, accessories, and compatibility with the approved project documents.",
        checks: [
          "Match the comparison to the same material and configuration direction.",
          "Separate an illustrative image or reference price from the final selected system.",
          "Confirm whether accessories, replacement parts, and related restroom items are part of the request.",
        ],
      },
      {
        title: "Make the quote boundary clear.",
        copy: "Reference pricing and an early budget can help scope a conversation, but final project pricing depends on the exact configuration and conditions. Confirm the final price, freight, tax, availability, delivery, installation scope, access, schedule, and project requirements before an order is accepted.",
        checks: [
          "Ask what is included in the quoted direction.",
          "Review the factors that still require project confirmation.",
          "Keep the final selection tied to the actual room and approved documents.",
        ],
      },
    ],
    nextSteps: [
      { href: "/projects", label: "Browse documented projects", copy: "Review owner-documented project records by state, material, and facility type." },
      { href: "/commercial-restroom-accessories", label: "Review accessory categories", copy: "Coordinate accessories with the partition scope rather than treating them as an afterthought." },
      { href: "/resources/mounting-styles", label: "Compare support systems", copy: "See the project conditions to confirm for the four common mounting styles." },
    ],
    quoteTitle: "Want to compare a real partition scope?",
    quoteCopy: "Send the project location, materials or models you are considering, quantities, plans or photos, and any unanswered questions. Partition Stalls will review the request around the actual job.",
  },
];

export function getBuyerGuide(slug?: string) {
  return buyerGuides.find((guide) => guide.slug === slug);
}
